import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Warehouse, Pencil } from "lucide-react";

const emptyForm = { name: "", address: "", notes: "" };

export default function MagazziniPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => setItems((await api.get("/warehouses")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (w) => {
    setEditingId(w.id);
    setForm({ name: w.name, address: w.address || "", notes: w.notes || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome obbligatorio");
    try {
      if (editingId) await api.patch(`/warehouses/${editingId}`, form);
      else await api.post("/warehouses", form);
      toast.success("Sede salvata");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare la sede?")) return;
    try { await api.delete(`/warehouses/${id}`); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{t("auto.anagrafica")}</div>
          <h1 className="mt-1 text-3xl font-bold text-slate-900 lg:text-4xl">{t("menu.warehouses")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auto.se_hai_più_sedi_operative_ogni_seriale_può_essere_associato_")}</p>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="btn-new-warehouse">
          <Plus className="mr-2 h-4 w-4" /> {t("auto.nuova_sede_btn")}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {items.map((w) => (
          <div key={w.id} className="rounded-sm border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <Warehouse className="mt-0.5 h-5 w-5 text-slate-500" />
                <div>
                  <div className="font-semibold text-slate-900">{w.name}</div>
                  <div className="mt-1 text-xs text-slate-500">{w.address || "—"}</div>
                  {w.notes && <div className="mt-2 text-xs text-slate-500 italic">{w.notes}</div>}
                </div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => openEdit(w)} data-testid={`edit-warehouse-${w.id}`}><Pencil className="h-3 w-3" /></Button>
                <Button size="sm" variant="ghost" onClick={() => del(w.id)}><Trash2 className="h-3 w-3 text-red-600" /></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full rounded-sm border border-dashed border-slate-300 p-10 text-center text-slate-400">{t("auto.nessuna_sede_configurata")}</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? t("auto.modifica_sede") : t("auto.nuova_sede_btn")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>{t("common.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="wh-name" /></div>
            <div><Label>{t("common.address")}</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rounded-sm" data-testid="wh-address" /></div>
            <div><Label>{t("common.notes")}</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm" /></div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-sm bg-[#0052FF] hover:bg-[#0040CC]" data-testid="wh-save">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
